import { useEffect, useState } from "react"
import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import type { CustomerFormValues } from "../lib/schemas/travel-booking"
import { customerSchema } from "../lib/schemas/travel-booking"
import { Button } from "@/components/ui/button"
import { ProductModal } from "./product-modal"
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogPortal, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { FormField } from "../ui-form/form-field"
import { useDebounce } from "use-debounce"
import { useMutation, useQuery } from "react-query"
import { adddNewContact, CustomerResponse, getCustomers } from "../apis"
import { CustomerOption } from "./customer-step"
import Select from "react-select"
import { AlertDialogOverlay } from "@radix-ui/react-alert-dialog"
import { step2 } from "../lib/FormConfigs"
import { renderField } from "../lib/FormConfigs/Renderer"
import { FieldConfig } from "../lib/FormConfigs/config"
import { useParams } from "react-router-dom"
import { AutoComplete } from "@/components/ui/autocomplete-search"
import { Spinner } from "components"

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (customer: CustomerFormValues) => void;
  initialData?: CustomerFormValues;
  config?: FieldConfig[];
  productConfig?: FieldConfig[];
}

const defaultCustomer: CustomerFormValues = {
  mobileNumber: "",
  fullName: "",
  email: "",
  pan: "",
  products: [],
};

export function CustomerModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  config,
  productConfig,
}: CustomerModalProps) {
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 800);
  const type_order = useParams()?.type as string;
  const configFile = config ? config : step2[type_order as keyof typeof step2];

  const { data: customerSuggestions, isLoading: isLoadingCustomers } = useQuery<
    CustomerResponse[]
  >({
    queryKey: ["customers", debouncedSearchTerm],
    queryFn: () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.trim() === "") {
        return [];
      }
      return getCustomers(searchTerm);
    },
    enabled: searchTerm.length >= 4 && debouncedSearchTerm.trim() !== "",
    staleTime: 1000,
  });

  const handleCustomerSelection = (selected: CustomerOption | null) => {
    if (selected) {
      // Populate form with selected customer data
      form.setValue("mobileNumber", selected.customer.phone_number);
      form.setValue("fullName", selected.customer.full_name);
      form.setValue("email", selected.customer.email);
      form.setValue("pan", selected.customer.pan_no);
    }
  };
  // Create a form specifically for this modal
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: initialData || defaultCustomer,
    mode: "onChange",
  });

  // Update form values when initialData changes
  useEffect(() => {
    if (initialData) {
      // Reset the form with the new initialData
      form.reset(initialData);
    } else {
      // Reset to default values if not editing
      form.reset(defaultCustomer);
    }
  }, [initialData, form]);

  const handleSubmit = (values: CustomerFormValues) => {
    onSubmit(values);
    form.reset(defaultCustomer);
    console.log("Print vlaue ===>>", values)
  };

  const handleAddProduct = (product: any) => {
    const currentProducts = form.getValues("products") || [];
    form.setValue("products", [...currentProducts, product], {
      shouldValidate: true,
    });
    setIsProductModalOpen(false);
  };

  const handleRemoveProduct = (index: number) => {
    const currentProducts = form.getValues("products") || [];
    form.setValue(
      "products",
      currentProducts.filter((_, i) => i !== index),
      { shouldValidate: true }
    );
  };

  // Get products with safe access
  const products = form.watch("products") || [];

  const {mutateAsync:addContactMutateAsync, isLoading:loader} = useMutation({
      mutationFn: adddNewContact,
      onSuccess: () => {
  
      },
      onError: () => {
      },
    })

    const handleCreateContact = async (data: CustomerFormValues) => {
      const { mobileNumber, fullName, email, pan } = data;
      const contactData = {
        name: fullName,
        pan_no: pan || "",
        phone_number: mobileNumber,
        email: email || "",
      };
      try {
        const response = await addContactMutateAsync(contactData);
        if (response?.id) {
          // If contact created successfully, update the form with new contact details
          form.setValue("customerId", response.id.toString());
          handleCustomerSelection({
            value: mobileNumber,
            label: mobileNumber,
            customer: {
              phone_number: mobileNumber,
              full_name: fullName,
              email: email || "",
              pan_no: pan || "",
              id: response.id.toString(),
            },
          });
        }
      } catch (error) {
      }
    }

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <AlertDialogPortal>
          {loader && <Spinner/>}
          <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0 z-50" />
          <AlertDialogContent className="max-w-[880px] p-0 rounded-lg bg-white-A700">
            <AlertDialogHeader className="flex text-left gap-4 p-4 border-b">
              {/* <AlertDialogTitle>{initialData ? `Edit Customer ${initialData?.fullName}` : `Add Customer`}</AlertDialogTitle> */}
              <div className="flex flex-col">
                <h2 className="text-xl font-semibold text-gray-900">
                  {initialData
                    ? `Edit Customer ${initialData?.fullName}`
                    : `Add Customer`}
                </h2>
                <p className="text-sm text-left text-gray-500 mt-1">
                  Fill the required details
                </p>
              </div>
            </AlertDialogHeader>
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="w-full max-h-[600px] overflow-auto">
                  <div className="space-y-4 py-4 px-10 max-w-md">
                    {configFile?.map((item, index) => {
                      if (item?.name === "mobileNumber")
                        return (
                          <FormField
                            key={index}
                            isOptionalField
                            name="mobileNumber"
                            label="What is the mobile number of customer?"
                            render={({ field }) => (
                              // <Select<CustomerOption>
                              //   options={
                              //     customerSuggestions?.map((customer) => ({
                              //       value: customer?.phone_number,
                              //       label: `${customer?.full_name} (${customer?.phone_number})`,
                              //       customer: {
                              //         phone_number: customer?.phone_number,
                              //         full_name: customer?.full_name,
                              //         email: customer?.email,
                              //         pan_no: customer?.pan_no,
                              //         id: customer?.id.toString(),
                              //       },
                              //     })) || []
                              //   }
                              //   onChange={(selected) => {
                              //     field.onChange(selected?.value || "");
                              //     form.setValue(
                              //       "customerId",
                              //       selected?.customer.id || ""
                              //     );
                              //     handleCustomerSelection(
                              //       selected as CustomerOption
                              //     );
                              //   }}
                              //   onInputChange={(inputValue) =>
                              //     setSearchTerm(inputValue)
                              //   }
                              //   isLoading={isLoadingCustomers}
                              //   placeholder="Search and select customer"
                              //   className="react-select-container"
                              //   classNamePrefix="react-select"
                              //   components={{ IndicatorSeparator: () => null }}
                              //   styles={{
                              //     control: (base, state) => ({
                              //       ...base,
                              //       padding: "8px",
                              //       borderRadius: "8px",
                              //       borderColor: state.isFocused
                              //         ? "#3b82f6"
                              //         : base.borderColor,
                              //       boxShadow: state.isFocused
                              //         ? "0 0 0 1px #3b82f6"
                              //         : base.boxShadow,
                              //       "&:hover": {
                              //         borderColor: "#3b82f6",
                              //       },
                              //     }),
                              //     valueContainer: (base) => ({
                              //       ...base,
                              //       padding: "0 8px",
                              //     }),
                              //     singleValue: (base) => ({
                              //       ...base,
                              //       fontSize: "14px",
                              //       fontWeight: 500,
                              //     }),
                              //   }}
                              // />
                              <AutoComplete
                               value={{
                                 value: field.value,
                                label: field.value,
                               }}                              
                                onValueChange={sel => {
                                  const customer = customerSuggestions?.find(
                                    (c) => c.id?.toString() === sel?.value
                                  );
                                  if (customer?.id) {
                                    field.onChange(customer?.phone_number || "");
                                    form.setValue(
                                      "customerId",
                                      customer.id.toString()
                                    );
                                    handleCustomerSelection({
                                      value: customer.phone_number,
                                      label: `${customer.full_name} (${customer.phone_number})`,
                                      customer: {
                                        phone_number: customer.phone_number,
                                        full_name: customer.full_name,
                                        email: customer.email,
                                        pan_no: customer.pan_no,
                                        id: customer.id.toString(),
                                      },
                                    });
                                  } else {
                                    form.setValue("customerId", "");
                                    field.onChange(sel.label || "");
                                  }
                                  setSearchTerm(field.value || "");
                                }}
                                options={
                                  customerSuggestions?.map((customer) => ({
                                    value: customer?.id.toString(),
                                    label: `${customer?.full_name} (${customer?.phone_number})`,
                                  })) || []
                                }

                                inputValue={searchTerm}
                                onInputChange={(value) => {
                                  if (value.length < 10 && form.getValues("customerId")) {
                                    form.setValue("customerId", "");
                                    handleCustomerSelection({
                                      value: form.getValues("mobileNumber"),
                                      label: form.getValues("mobileNumber"),
                                      customer: {
                                        phone_number: '',
                                        full_name: '',
                                        email: '',
                                        pan_no: '',
                                        id: '',
                                      },
                                    });
                                  }
                                  setSearchTerm(value);
                                  field.onChange(value);
                                }}
                                emptyMessage="No customers found"
                                isLoading={isLoadingCustomers}
                                placeholder="Search and select customer"
                              />
                            )}
                          />
                        );
                      return (
                        <FormField
                          key={index}
                          isOptionalField={item.required}
                          name={item.name}
                          label={item.label}
                          render={({ field }) => renderField(item, field)}
                        />
                      );
                    })}
                    {products?.length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-sm font-medium mb-2">Products</h3>
                        <div className="border rounded-md p-3 space-y-2">
                          {products.map((product, index) => (
                            <div
                              key={index}
                              className="text-sm p-2 bg-muted rounded-md flex justify-between items-start"
                            >
                              <div>
                                <p>
                                  <strong>Type:</strong>{" "}
                                  {product?.productType?.label || "Unknown"}
                                </p>
                                <p>
                                  <strong>Currencies:</strong>{" "}
                                  {product?.currencies
                                    ?.map(
                                      (c) =>
                                        `${c?.currency?.label || "Unknown"} (${
                                          c?.amount || 0
                                        })`
                                    )
                                    .join(", ") || "None"}
                                </p>
                                <p>
                                  <strong>Total Value:</strong>{" "}
                                  {product?.currencies
                                    ?.reduce(
                                      (sum, c) =>
                                        sum + (c?.amount || 0) * (c?.rate || 1),
                                      0
                                    )
                                    .toFixed(2) || "0.00"}
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveProduct(index)}
                                className="text-destructive"
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <AlertDialogFooter className="h-min flex items-center gap-2 flex-row p-4 bordert-t">
                  <Button
                    className="text-blue-700 border border-blue-700"
                    type="button"
                    variant="outline"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  {products?.length > 0 && (
                    <Button
                      className="bg-blue-700 text-white-A700 border border-blue-700"
                      type="submit"
                    >
                      Save
                    </Button>
                  )}
                  <Button
                    className="bg-blue-700 text-white-A700 border border-blue-700"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if(form.getValues("customerId")) {
                        setIsProductModalOpen(true);
                      } else {
                        handleCreateContact(form.getValues());
                        setIsProductModalOpen(true);
                      }
                    }}
                  >
                    Continue to add product
                  </Button>
                </AlertDialogFooter>
              </form>
            </FormProvider>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>

      {isProductModalOpen && (
        <ProductModal
          config={productConfig}
          isOpen={isProductModalOpen}
          onClose={() => setIsProductModalOpen(false)}
          onSubmit={handleAddProduct}
        />
      )}
    </>
  );
}
